import { cn } from "@minket/ui/lib/utils";

/** The outline's box (@minket/brand/outlines, `wordmarkOutline`): wordmark.test.ts holds them equal. */
export const WORDMARK_VIEWBOX = "0 0 3829 736";
const ASPECT = 3829 / 736;

/**
 * The logo: the seal mark beside the wordmark, as one lockup wherever the console names itself
 * (the owner, 2026-10-29). The wordmark is never live text (direction §6): @minket/brand's
 * outline in the ink of its surface (`currentColor`), at least 14 px tall: 22 px on the rail, 17
 * in the phone bar. The seal mark is drawn at 40 px, the smallest size its bands are drawn for,
 * its m in `currentColor` and its bands in the seal ink, so it follows light and dark with the
 * page. Both are served once as symbols (`public/wordmark.svg`, `public/seal-mark-40.svg`) and
 * drawn with `<use>`, so their path data is cached rather than carried in every route.
 *
 * Inside a link it is decorative and the link names itself ("Minket overview"); on its own,
 * `title` makes it an image with that name. `mark={false}` draws the wordmark alone.
 */
export function Wordmark({
  height = 18,
  title,
  mark = true,
  className,
}: {
  height?: number;
  title?: string;
  mark?: boolean;
  className?: string;
}) {
  const h = Math.max(14, height);
  const word = (
    <svg
      viewBox={WORDMARK_VIEWBOX}
      width={Math.round(h * ASPECT * 10) / 10}
      height={h}
      aria-hidden
      focusable="false"
      className="block shrink-0"
    >
      <use href="/wordmark.svg#wordmark" />
    </svg>
  );
  const lockup = (
    <>
      {mark ? (
        <svg width={40} height={40} aria-hidden focusable="false" className="block shrink-0">
          <use href="/seal-mark-40.svg#mark" />
        </svg>
      ) : null}
      {word}
    </>
  );
  const box = cn("inline-flex shrink-0 items-center gap-2.5", className);
  return title ? (
    <span role="img" aria-label={title} className={box}>
      {lockup}
    </span>
  ) : (
    <span aria-hidden className={box}>
      {lockup}
    </span>
  );
}
